import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";

import { ILead, LeadRoutingOutcome, isDuplicateRouting } from "../../models/Lead";

/**
 * Shown at the top of a lead the widget created for a vehicle another booking already held.
 * SAV only: the caller gates it, and the API blanks the fields for anyone else. Renders nothing on
 * an ordinary lead, so it is safe to mount unconditionally.
 *
 * The first booking usually lives in another yard's Wilma, so it is only linked when it is in this
 * one. Otherwise the banner names who holds it.
 */
export default function DuplicateVehicleBanner({
  lead,
  holderName,
  leadListPath,
}: {
  lead: ILead;
  holderName?: string;
  leadListPath: string;
}) {
  const { t } = useTranslation("leads");

  if (!isDuplicateRouting(lead.routingOutcome)) return null;

  const diverted = lead.routingOutcome === LeadRoutingOutcome.DivertedDuplicateVrm;

  return (
    <div role="status" className="rounded-md border border-amber-200 bg-amber-50 p-4">
      <div className="flex gap-3">
        <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-amber-600" aria-hidden="true" />
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-amber-900">
            {diverted
              ? t("Second booking for this vehicle - sent to SAV")
              : t("Second booking for this vehicle")}
          </h3>

          {lead.routingReason && (
            <p className="mt-1 text-sm text-amber-800">{lead.routingReason}</p>
          )}

          <p className="mt-2 text-sm text-amber-800">
            {t("Check the other booking before working this one. The vehicle can only be collected once.")}
          </p>

          {lead.duplicateOfLeadId && (
            lead.duplicateOfLeadIsLocal ? (
              <Link
                to={`${leadListPath}/${lead.duplicateOfLeadId}`}
                className="mt-2 inline-flex items-center text-sm font-medium text-amber-900 underline underline-offset-2 hover:text-amber-950"
              >
                {t("Open the booking that already holds this vehicle")}
              </Link>
            ) : (
              <p className="mt-2 text-sm text-amber-900 break-all">
                <span className="font-medium">
                  {holderName
                    ? t("Already held by {{name}}", { name: holderName })
                    : t("Already held in another yard's system")}
                </span>
                {" · "}
                {lead.duplicateOfLeadId}
              </p>
            )
          )}
        </div>
      </div>
    </div>
  );
}
